import { useContext, useLayoutEffect, useMemo, useRef, useCallback, useState } from 'react';
import { useMaskedOnChange } from './useMaskedOnChange.js';
import ComboBoxContext from '../../ComboBoxCTX.js';
import { isSelectedValueEmpty } from '../../utils/listHelper.js';

interface UseControlsInputT {
  handleOnBlur: () => void;
  handleOnChange?: React.ChangeEventHandler<HTMLInputElement>;
  handleOnFocus: React.FocusEventHandler<HTMLInputElement>;
  showPlaceholder: string | null | undefined;
  spanReferenceText: string | undefined;
  width: number;
  spanReference: React.Ref<HTMLSpanElement>;
}

const PADDING_EXTRA = 2; // espacio para el caret

const useControlsInput = (): UseControlsInputT => {
  const {
    props: { selectedValues, allOptions, placeholder, onFilter, inline },
    setHasFocus,
    inputValue,
    setInputValue,
  } = useContext(ComboBoxContext);

  const [width, setWidth] = useState(1);
  const spanReference = useRef<HTMLSpanElement>(null);

  const showPlaceholder = !inputValue && (inline || isSelectedValueEmpty(selectedValues)) ? placeholder : null;

  const spanReferenceText = (inputValue || '').replace(/\s/g, '\u00a0') || '';

  useLayoutEffect(() => {
    const el = spanReference.current;
    if (!el) return;

    const measured = Math.ceil(el.offsetWidth) + PADDING_EXTRA;

    if (Math.abs(measured - width) >= 1) {
      setWidth(measured);
    }
  }, [spanReferenceText, width]);

  const handleOnChange = useMaskedOnChange();

  const handleOnBlur = useCallback(() => {
    setInputValue('');
    if (onFilter) onFilter(allOptions);
    setHasFocus(false);
  }, [setInputValue, onFilter, allOptions, setHasFocus]);

  const handleOnFocus = useCallback(() => setHasFocus(true), [setHasFocus]);

  return useMemo(
    () => ({
      handleOnBlur,
      handleOnChange,
      handleOnFocus,
      showPlaceholder,
      spanReferenceText,
      width,
      spanReference,
    }),
    [handleOnBlur, handleOnChange, handleOnFocus, showPlaceholder, spanReferenceText, width],
  );
};

export { useControlsInput };
